<template>
  <modal
    :title="team.name + ' Members'"
    :show="show"
    submit-button-text="Done"
    @hidden="$emit('input', false)"
    :hide-footer="true"
    modalClass="manage-members-modal"
  >
    <div class="d-flex flex-column">
      <label class="text-normal bold">Invite members</label>
      <searchable-list
        :options="searchText ? selectableUsersList : []"
        @search="onSearch"
        @select="onMemberSelect"
      >
        <template v-slot:default="{ option }">
          <div class="d-flex justify-content-between align-items-center">
            <div v-if="option.type === 'exists'" @click.stop>
              user - {{ searchText }} is already part of the team
            </div>
            <div class="overflow-hidden" v-else-if="option.type === 'newUser'" @click.stop>
              <div class="d-flex">
                <div class="plus-icon-container"><i class="plus-icon"></i></div>
                <div class="flex-grow-1 pl-2 overflow-hidden">
                  <div class="text-truncate">{{ option.text }}</div>
                  <div v-if="!option.isEmail">Type an email to invite</div>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center" @click.stop v-else>
              <user-short :user-id="option.user.id" length="24"></user-short>
              <span class="pl-2 user-text" v-no-capture>{{ option.user.displayName }}</span>
            </div>
            <div class="text-nowrap" v-if="option.type === 'newUser' && option.isEmail" @click.stop>
              <div class="ml-2" v-if="invitedList.indexOf(searchText) > -1">
                <i class="checkmark-icon"></i>
                <span class="text-gradient pl-2">Invited</span>
              </div>
              <b-button
                @click="inviteUser(searchText)"
                variant="link"
                class="text-decoration-none"
                v-else
              >
                Invite
              </b-button>
            </div>
            <b-button
              @click="onMemberSelect(option)"
              v-else-if="option.type !== 'newUser' && option.type !== 'exists'"
              variant="link"
              class="text-decoration-none"
            >
              Add
            </b-button>
          </div>
        </template>
      </searchable-list>
    </div>
    <div class="pt-5">
      <h2 class="text-normal">Current members</h2>
      <ul class="added-members">
        <member-row
          :org-id="orgId"
          :team-id="teamId"
          :member="member"
          v-for="member in membersList"
          :key="member.user.id"
          @update="onMemberUpdate"
          @remove="refreshMembersList"
        />
      </ul>
    </div>
    <max-users-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
  </modal>
</template>

<script>
import MemberRow from '@/components/MemberRow'
import Modal from '@/components/Modal'
import MaxUsersPaywallModal from '@/components/paywalls/MaxUsersPaywallModal.vue'
import SearchableList from '@/components/SearchableList'
import UserShort from '@/components/UserShort'
import { featureFlags } from '@/constants/featureFlags'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import { BButton } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component shows up in a modal. It can be used to add/invite member to the team
 */
export default {
  name: 'ManageTeamMembers',
  props: ['show', 'orgId', 'teamId'],
  model: {
    prop: 'show'
  },
  components: {
    BButton,
    SearchableList,
    UserShort,
    Modal,
    MemberRow,
    MaxUsersPaywallModal
  },
  data() {
    return {
      searchText: '',
      invitedList: [], // this is a local list that contains list of users already invited
      membersList: [],
      isInviteAllowed: true,
      isPaywallModalShown: false
    }
  },
  mixins: [FeaturesMixin],
  computed: {
    orgMembers() {
      const members = this.$store.getters.orgMemberships(this.orgId)
      return R.sort(({ user: a }, { user: b }) => {
        return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
      }, members)
    },
    teamMemberships() {
      return this.team.memberships || []
    },
    selectableUsersList() {
      const usersNotPartOfTeam = R.differenceWith(
        (x, y) => x.user.id === y.user.id,
        this.orgMembers,
        this.teamMemberships
      )
      let list = usersNotPartOfTeam.filter(x =>
        [...x.user.firstName.split(' '), ...x.user.lastName.split(' '), x.user.email].some(item =>
          item.toLowerCase().startsWith(this.searchText.toLowerCase())
        )
      )
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
      if (list.length === 0 && this.searchText) {
        const isEmail = emailRegex.test(this.searchText)
        //check if user is already part of the team
        const exists =
          this.teamMemberships.findIndex(
            x => x.user.email.toLowerCase() === this.searchText.toLowerCase()
          ) > -1
        if (exists) {
          return [
            {
              type: 'exists'
            }
          ]
        } else {
          return [
            {
              type: 'newUser',
              text: this.searchText,
              isEmail
            }
          ]
        }
      } else return list.map(user => ({ ...user, id: user.userId }))
    },
    team() {
      return this.$store.getters.teamById(this.teamId) || {}
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    }
  },
  methods: {
    ...mapActions(['getOrgMemberships', 'getTeamMemberships', 'addMemberToTeam', 'inviteUsers']),
    async wrapOverError(fn) {
      try {
        return await fn()
      } catch (e) {
        let errors = R.pathOr([DEFAULT_ERROR], ['response', 'data', 'detail'], e)
        if (typeof errors === 'string') {
          errors = [errors]
        } else if (errors.user) {
          errors = errors.user
        }
        errors.forEach(error => {
          EventBus.$emit('ADD_TOAST', {
            message: error,
            type: TOAST_TYPE.ERROR
          })
        })
      }
    },
    onSearch(searchText) {
      this.searchText = searchText
    },
    async onMemberSelect(member) {
      if (member.type === 'newUser') {
        if (member.isEmail) {
          this.inviteUser(member.text)
        }
        return
      }
      const addMemberToTeam = this.addMemberToTeam.bind(this, {
        orgId: this.orgId,
        teamId: R.path(['team', 'id'], this),
        userId: member.userId,
        type: 'regular'
      })
      const response = await this.wrapOverError(addMemberToTeam)
      if (response) {
        EventBus.$emit('ADD_TOAST', { message: 'Member successfully added to the team' })
        this.refreshMembersList()
      }
    },
    showPaywallModal() {
      this.isPaywallModalShown = true
    },
    async inviteUser(email) {
      if (email && this.invitedList.indexOf(email) === -1) {
        if (!this.isInviteAllowed) {
          this.showPaywallModal()
          return
        }
        const email = this.searchText
        const inviteUsers = this.inviteUsers.bind(this, {
          orgId: this.orgId,
          teamId: this.teamId,
          emails: [email]
        })
        const response = await this.wrapOverError(inviteUsers)
        if (response) {
          EventBus.$emit('ADD_TOAST', { message: 'Invite sent successfully' })
          this.invitedList.push(email)
        }
      }
    },
    refreshMembersList() {
      let [owners, regulars] = R.partition(R.propEq('type', 'owner'), this.teamMemberships)
      const sortByFullName = R.sortBy(
        R.compose(
          R.toLower,
          R.path(['user', 'displayName'])
        )
      )
      this.membersList = [...sortByFullName(owners), ...sortByFullName(regulars)]
    },
    onMemberUpdate(member) {
      const changedIndex = this.membersList.findIndex(R.propEq('userId', member.userId))
      this.membersList = R.update(changedIndex, member, this.membersList)
    }
  },
  async mounted() {
    this.isInviteAllowed = await this.satisfyFeature(this.orgId, featureFlags.MAX_USER_COUNT)
    if (this.orgMembers.length === 0) {
      this.getOrgMemberships({ id: this.orgId })
    }
    if (!this.team.memberships) {
      await this.getTeamMemberships({ orgId: this.org.id, teamId: this.teamId })
    }
    this.refreshMembersList()
  },
  watch: {
    teamMemberships(newMembers, oldMembers) {
      if (!oldMembers.length && newMembers.length) {
        this.refreshMembersList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/forms';
@import '~@/styles/_styleConfigurations.scss';

/deep/ .add-member {
  padding: 20px;
}

.added-members {
  padding: 0;
}

.plus-icon-container {
  background-color: $brandTertiary;
  height: 24px;
  width: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}
</style>
<style lang="scss">
.manage-members-modal {
  min-height: 500px;
}
</style>
